/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	FileIcon,
	FileIconSimple,
} from '@atlassian/conversation-assistant-ui-components/ui/file-icon';
import { PreviewCard } from '@atlassian/conversation-assistant-ui-components/ui/PreviewCard';

import { isTextFile } from '../../../utils/fileTypeHelpers';
import { looksLikeImage } from '../../../utils/looksLikeImage';
import { FilePreviewModal } from '../../file-preview-modal';
import messages from './messages';

const MAX_TEXT_SIZE = 2 * 1024 * 1024;

type FileCardProps = {
	isDisabled?: boolean;
	fileName: string;
	isLoading?: boolean;
	error?: string;
	onRemove?: () => void;
	autoScroll?: boolean;
	/**
	 * Preview blob URL (generated by attachment store)
	 */
	previewBlobUrl?: string;
	/**
	 * Preview loading state (managed by attachment store)
	 */
	isPreviewLoading?: boolean;
	/**
	 * File object for full preview in modal
	 */
	fileObject?: File;
};

const styles = cssMap({
	fileCardBgWrapper: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.small'),
		display: 'flex',
	},
});

const FileCardBgWrappper = ({ children }: { children: React.ReactNode }) => {
	return <Box xcss={styles.fileCardBgWrapper}>{children}</Box>;
};

const getFileNameAndExtension = (fileName: string) => {
	const lastDotIndex = fileName.lastIndexOf('.');

	if (lastDotIndex === -1 || lastDotIndex === 0) {
		return { name: fileName, extension: '' };
	}

	return { name: fileName.slice(0, lastDotIndex), extension: fileName.slice(lastDotIndex + 1) };
};

export const FileCard = ({
	isDisabled = false,
	fileName,
	onRemove,
	isLoading,
	error,
	autoScroll,
	previewBlobUrl,
	isPreviewLoading,
	fileObject,
}: FileCardProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { name: fileNameWithoutExtension, extension } = getFileNameAndExtension(fileName);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [textContent, setTextContent] = useState<string | null>(null);
	const [isLoadingText, setIsLoadingText] = useState(false);

	const handleCardClick = useCallback(() => {
		if (!fileObject) {
			return;
		}

		if (isTextFile(fileObject.type, fileObject.name)) {
			sendAnalyticsEvent({
				action: 'opened',
				actionSubject: 'preview',
				actionSubjectId: 'filePreview',
				attributes: {
					fileType: fileObject.type,
					fileSize: fileObject.size,
				},
			});

			setIsLoadingText(true);
			setIsModalOpen(true);

			// Performance safeguard: Limit text file preview to 2MB
			if (fileObject.size > MAX_TEXT_SIZE) {
				const sizeMB = (fileObject.size / (1024 * 1024)).toFixed(1);
				setTextContent(formatMessage(messages.textFileTooLarge, { sizeMB }));
				setIsLoadingText(false);
				return;
			}

			fileObject
				.text()
				.then((content) => {
					setTextContent(content);
					setIsLoadingText(false);
				})
				.catch((error) => {
					logException({
						exception: error,
						name: 'text-file-content-load-error',
						tags: {
							fileType: fileObject.type,
						},
					});

					const errorMessage =
						error instanceof Error ? error.message : formatMessage(messages.textLoadFailed);
					setTextContent(errorMessage);
					setIsLoadingText(false);
				});
		} else if (looksLikeImage(fileObject) || fileObject.type === 'application/pdf') {
			sendAnalyticsEvent({
				action: 'opened',
				actionSubject: 'preview',
				actionSubjectId: 'filePreview',
				attributes: {
					fileType: fileObject.type,
					fileSize: fileObject.size,
				},
			});

			setIsModalOpen(true);
		}
	}, [fileObject, formatMessage, sendAnalyticsEvent]);

	const isPreviewableType =
		fileObject &&
		(looksLikeImage(fileObject) ||
			fileObject.type === 'application/pdf' ||
			isTextFile(fileObject.type, fileObject.name));

	const canOpenPreview =
		!isDisabled && isPreviewableType && !isLoading && !isPreviewLoading && !error;

	return (
		<>
			<PreviewCard
				autoScroll={autoScroll}
				label={fileNameWithoutExtension}
				subLabel={extension?.toUpperCase()}
				isLoading={isLoading || isPreviewLoading}
				error={error}
				onRemove={isDisabled ? undefined : onRemove}
				imageSrc={previewBlobUrl}
				onClick={canOpenPreview ? handleCardClick : undefined}
				cardIcon={
					<FileCardBgWrappper>
						<FileIcon fileName={fileName} size="medium" />
					</FileCardBgWrappper>
				}
				subLabelIcon={
					<FileIconSimple size="small" fileName={fileName} color={token('color.icon.subtle')} />
				}
				errorLabel={formatMessage(messages.fileErrorIconLabel)}
				removeLabel={formatMessage(messages.removeFileLabel, { fileName })}
			/>
			{canOpenPreview && (
				<FilePreviewModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					fileName={fileName}
					fileObject={fileObject}
					previewBlobUrl={previewBlobUrl}
					textContent={textContent}
					isLoadingText={isLoadingText}
				/>
			)}
		</>
	);
};
